import { type UseQueryResult, useQuery } from 'react-query';
import {
  RolePublishService,
  PagedResultDto_PublishedRolesGroupedVm,
  Wealthlane_CustomerService_AppServices_CustomerSuccess_PublishedRolesVm,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { RolePublishFilterParamsType, type TableParamsType } from '../../types/common';

export type FlattenedPublishedRole = Wealthlane_CustomerService_AppServices_CustomerSuccess_PublishedRolesVm & {
  sourceCompanyId?: string;
  sourceRoleId?: string;
  sourceCompanyName?: string | null;
  sourceRoleName?: string | null;
  publishedRoles?: Wealthlane_CustomerService_AppServices_CustomerSuccess_PublishedRolesVm[];
};

export type FlattenedPublishedRolesPagedResult = {
  items?: FlattenedPublishedRole[];
  totalCount?: number;
};

const useGetRolePublishList = (
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  tableInfo: TableParamsType
): UseQueryResult<FlattenedPublishedRolesPagedResult | undefined, unknown> => {
  const { filter, sorting, skipCount, maxResultCount, companyName, roleName, _force } = tableInfo;

  const rolePublishList = useQuery(
    [
      'getRolePublishList',
      companyName,
      roleName,
      filter,
      sorting,
      skipCount,
      maxResultCount,
      _force,
    ],
    async () => {
      const response = await handleRequest(
        RolePublishService.getApiCustomerApiV2CustomerCustomerSuccessRolesPublished(
          companyName,
          roleName,
          filter,
          sorting,
          skipCount,
          maxResultCount
        )
      );

      if (!response || !response.items) {
        return response as FlattenedPublishedRolesPagedResult;
      }

      // Flatten the grouped data
      const flattenedItems: FlattenedPublishedRole[] = [];
      
      response.items.forEach((group) => {
        if (group.publishedRoles) {
          group.publishedRoles.forEach((publishedRole) => {
            flattenedItems.push({
              ...publishedRole,
              sourceCompanyId: group.sourceCompanyId,
              sourceRoleId: group.sourceRoleId,
              sourceCompanyName: group.sourceCompanyName,
              sourceRoleName: group.sourceRoleName,
              publishedRoles: group.publishedRoles,
            });
          });
        }
      });

      return {
        items: flattenedItems,
        totalCount: response.totalCount,
      };
    }
  );

  return rolePublishList;
};

export default useGetRolePublishList;
